import { ThemedTable } from "../../../common/tables/ThemedTable";
import { muiBadgesDataTableTheme } from "../../../dashboard/reports/components/table/utils/theme";
import { useCompanyBadgesTableConfig } from "./hooks/useCompanyBadgesTableConfig";
import { useGetCompanyBadges } from "./hooks/useGetCompanyBadges";
import { Box } from "@mui/material";
import { TableToolbarSelect } from "./components/selectedToolbar/TableToolbarSelect";
import { TableToolbar as BadgesTableToolbar } from "./components/toolbar/TableToolbar";
import { usePrintDialog } from "./components/hooks/usePrintDialog";
import { PrintDialog } from "./components/printDialog/PrintDialog";
import { IPersonBadge, IProduct } from "../../../../common/types";
import { DeactivateDialog } from "./components/deactivateDialog/DeactivateDialog";
import { useDeactivateDialog } from "./components/hooks/useDeactivateDialog";
import { MUIDataTableCheckboxProps } from "mui-datatables";
import { StatusCheckboxTable } from "./components/tableCheckbox/StatusCheckboxTable";
import React, { useEffect, useState } from "react";
import { CompanySearchResult } from "../../../search/types";
import { StaffOverrideCameraDialog } from "../../../common/dialog/staffOverrideCameraDialog/StaffOverrideCameraDialog";
import { BadgePaymentStatus } from "../../../../common/enums";
import { useTranslation } from "react-i18next";
import JWTHelper from "../../../../common/helpers/jwt/JWTHelper";
import { useUpgradeDialog } from "../../../search/hooks/useUpgradeDialog";
import { UpgradeDialogStep, UpgradeDialogWithPartialPayments } from "@reedexpo/mercury-badge-manager-components";
import EventHelper from "../../../../common/network/helpers/event/EventHelper";
import MagentoHelper from "../../../../common/network/helpers/magento/MagentoHelper";
import PaymentApproveDialog from "../../../common/approvePayment/PaymentApproveDialog";
import { usePaymentApprove } from "../../../common/hooks/usePaymentApprove";
import { useUnpaidBadgePayment } from "../../../search/hooks/useUnpaidBadgePayment/useUnpaidBadgePayment";
import { BADGE_PRINTED_AT } from "../../../../common/constants";
import { useSelector } from "react-redux";
import { productsSelector } from "../../../../redux/magento/selectors/magento.selectors";

type Props = {
  selectedCompany?: CompanySearchResult;
};

export const CompanyBadgesTable = ({ selectedCompany }: Props) => {
  const { i18n, t } = useTranslation("common");
  const [isOverrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [isStaffCameraReprintDialogOpened, setStaffCameraReprintDialogOpened] = useState(false);

  const products: IProduct[] = useSelector(productsSelector);

  const { tableData, getBadgesFromTableData, filterGetters, filterSetters, resetTableData, updateBadgesPaymentStatuses } =
    useGetCompanyBadges({ selectedCompany });
  const { columns, options, selectedRows, deselectRows, getBadgeStatusByCheckboxProps } = useCompanyBadgesTableConfig(
    tableData,
    resetTableData
  );

  const [unpaidBadgesToPrint, setUnpaidBadgesToPrint] = useState<IPersonBadge[]>([]);
  const {
    openPrintDialog,
    isPrintDialogOpened,
    closePrintDialog,
    setBadgesToPrint,
    badgesToPrint,
    isCameraOverrideDialogOpened,
    handleCloseOverrideCameraDialog,
    handleOpenOverrideCameraDialog,
    handleStaffOverride
  } = usePrintDialog();
  const { isDeactivateDialogOpened, closeDeactivateDialog, openDeactivateDialog, badgesToDeactivate } = useDeactivateDialog();

  const checkIfNotFirstPASPlusPrint = (badge: IPersonBadge) => {
    const badgeProduct = products.find(product => product.sku === badge.productCode);

    const isBadgePASPlus = badgeProduct?.rx_print_at === BADGE_PRINTED_AT.PRINTED_AT_SHOW_PLUS;
    const wasBadgePrintedAlready = !!badge.lastPrintedDate;

    return isBadgePASPlus && wasBadgePrintedAlready;
  };

  const checkIfAnyBadgeCannotBePrinted = () => {
    let isAnyBadgeUnavailableToPrint = false;

    badgesToPrint.forEach(badge => {
      if (!isAnyBadgeUnavailableToPrint) {
        isAnyBadgeUnavailableToPrint = checkIfNotFirstPASPlusPrint(badge);
      }
    });

    return isAnyBadgeUnavailableToPrint;
  };

  const handlePrintDialogOpen = () => {
    const isAnyBadgeUnavailableToPrint = checkIfAnyBadgeCannotBePrinted();

    if (isAnyBadgeUnavailableToPrint) {
      setStaffCameraReprintDialogOpened(true);
    } else {
      openPrintDialog();
    }
  };

  const checkForUnpaidBadges = (badges: IPersonBadge[]) => {
    return badges.filter(badge => badge.paymentStatus === BadgePaymentStatus.UNPAID);
  };

  const handleOverrideSuccess = async () => {
    await handleStaffOverride();
    if (selectedCompany) updateBadgesPaymentStatuses(BadgePaymentStatus.UNPAID_OVERRIDDEN, unpaidBadgesToPrint, selectedCompany);
  };

  const handlePaymentOverrideSuccess = () => {
    handlePaymentOverride();
    if (selectedCompany) updateBadgesPaymentStatuses(BadgePaymentStatus.UNPAID_OVERRIDDEN, selectedRows.badges, selectedCompany);
    setOverrideDialogOpen(false);
    toggleMakePaymentDialog();
  };

  const onStaffCameraReprintSuccess = () => {
    setStaffCameraReprintDialogOpened(false);
    openPrintDialog();
  };

  useEffect(() => {
    deselectRows();
  }, [
    filterGetters.selectedBadgeStatus,
    filterGetters.selectedProduct,
    filterGetters.selectedCustomerType,
    filterGetters.selectedBadgePaymentStatus
  ]);

  useEffect(() => {
    setUnpaidBadgesToPrint(checkForUnpaidBadges(selectedRows.badges));
    setBadgesToPrint(selectedRows.badges);
  }, [selectedRows]);

  const { getOrdersForBadges, upgradeDialogOpen, toggleDialog: toggleMakePaymentDialog } = useUpgradeDialog({});
  const { handleUnpaidBadgePayment, handlePaymentOverride } = useUnpaidBadgePayment({
    selectedBadges: selectedRows.badges,
    updateSelectedBadges: () => updateBadgesPaymentStatuses(BadgePaymentStatus.PAID, selectedRows.badges, selectedCompany!),
    deselectRows
  });

  const getDatatableComponents = () => {
    return {
      Checkbox: (props: MUIDataTableCheckboxProps) => (
        <StatusCheckboxTable status={getBadgeStatusByCheckboxProps(props)} props={{ ...props }} />
      ),
      TableToolbar: () => (
        <BadgesTableToolbar
          tableData={tableData}
          filterSetters={filterSetters}
          filterGetters={filterGetters}
          selectedCompany={selectedCompany}
        />
      ),
      TableToolbarSelect: () => {
        return (
          <TableToolbarSelect
            selectedRows={selectedRows}
            openDeactivateDialog={openDeactivateDialog}
            openPrintDialog={handlePrintDialogOpen}
            filterSetters={filterSetters}
            filterGetters={filterGetters}
            onMakePaymentClick={toggleMakePaymentDialog}
          />
        );
      }
    };
  };

  const tableComponents = getDatatableComponents();

  const handleOverridePaymentDialogClose = () => {
    setOverrideDialogOpen(false);
  };
  const handleCameraReprintDialogClose = () => setStaffCameraReprintDialogOpened(false);

  const { isPaymentApproved, isScannerOpened, paymentOptions, handleClose, handleSuccess, handlePaymentApprove } = usePaymentApprove();

  const handleOverridePayment = () => {
    setOverrideDialogOpen(true);
  };

  return (
    <>
      {isOverrideDialogOpen && (
        <StaffOverrideCameraDialog
          open={isOverrideDialogOpen}
          handleClose={handleOverridePaymentDialogClose}
          onSuccess={handlePaymentOverrideSuccess}
          alertMessage={t("search.overridePaymentDialog.warningMessage")}
          title={t("search.overridePaymentDialog.title")}
          badges={selectedRows.badges}
          onBackButtonClick={handleOverridePaymentDialogClose}
          successOnMount={JWTHelper.isPaymentOverrideDisabled()}
        />
      )}
      {isStaffCameraReprintDialogOpened && (
        <StaffOverrideCameraDialog
          open={isStaffCameraReprintDialogOpened}
          handleClose={handleCameraReprintDialogClose}
          onSuccess={onStaffCameraReprintSuccess}
          alertMessage={t("common:search.staffOverrideReprintDialog.warningMessage")}
          title={t("common:search.staffOverrideReprintDialog.title")}
          badges={badgesToPrint}
          successOnMount={JWTHelper.isPaymentOverrideDisabled()}
        />
      )}
      {upgradeDialogOpen && (
        <UpgradeDialogWithPartialPayments
          options={{
            enableReceiptPrint: true,
            initialPage: UpgradeDialogStep.REVIEW,
            isReviewBackButtonHidden: true,
            isPriceFromOrders: true,
            hideProductReviewName: true,
            skipSuccessPage: true,
            isPaymentApproved,
            onPaymentOverrideClick: handleOverridePayment,
            handlePaymentApprove,
            isDebitCardAvailable: true
          }}
          key={String(upgradeDialogOpen)}
          closeDialog={toggleMakePaymentDialog}
          open={upgradeDialogOpen}
          eventEdition={EventHelper.eventEdition}
          products={MagentoHelper.magentoProducts}
          currentProductSku={selectedRows.badges[0]!.productCode}
          currentCustomerType={selectedRows.badges[0]!.customerType}
          onUpdateComplete={handleUnpaidBadgePayment}
          badgeOrders={getOrdersForBadges(selectedRows.badges)}
          locale={i18n.language}
          badges={selectedRows.badges}
          disablePaymentLink
          environment={process.env.REACT_APP_ENVIRONMENT}
        />
      )}
      <PaymentApproveDialog
        skipUserAccessCheck
        badges={selectedRows.badges}
        paymentOptions={paymentOptions}
        isScannerOpened={isScannerOpened}
        handleClose={handleClose}
        handleSuccess={handleSuccess}
        successOnMount={JWTHelper.isPaymentOverrideDisabled()}
      />

      <DeactivateDialog
        deselectRows={deselectRows}
        badgesToDeactivate={badgesToDeactivate}
        onClose={closeDeactivateDialog}
        open={isDeactivateDialogOpened}
        filterSetters={filterSetters}
      />
      <StaffOverrideCameraDialog
        open={isCameraOverrideDialogOpened}
        title={t("companyPage.staffOverrideModal.title")}
        alertMessage={t("companyPage.staffOverrideModal.alert", {
          unpaidBadgesLength: unpaidBadgesToPrint.length,
          badgesToPrintLength: badgesToPrint.length
        })}
        handleClose={handleCloseOverrideCameraDialog}
        onSuccess={handleOverrideSuccess}
        badges={badgesToPrint}
        successOnMount={JWTHelper.isPaymentOverrideDisabled()}
      />
      <PrintDialog
        badges={getBadgesFromTableData()}
        badgesToPrint={badgesToPrint}
        open={isPrintDialogOpened}
        onClose={closePrintDialog}
        unpaidBadges={unpaidBadgesToPrint}
        handleOpenOverrideCameraDialog={handleOpenOverrideCameraDialog}
      />
      <Box sx={{ marginTop: 3 }}>
        <ThemedTable
          key={tableData.length}
          title={
            <BadgesTableToolbar
              tableData={tableData}
              filterSetters={filterSetters}
              filterGetters={filterGetters}
              selectedCompany={selectedCompany}
            />
          }
          theme={muiBadgesDataTableTheme}
          components={tableComponents}
          cols={columns}
          tableData={tableData}
          options={options}
        />
      </Box>
    </>
  );
};
